// Player Profile API Service
// Handles all player profile-related API calls

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Helper function to get auth headers
const getAuthHeaders = async () => {
  try {
    // Get the current Firebase user
    const { auth } = await import('../../firebaseConfig');
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      // Get the Firebase ID token
      const token = await currentUser.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  
  // Fallback to localStorage if Firebase token not available
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    
    // Log error details for debugging
    console.error('API Error:', {
      status: response.status,
      error: error
    });
    
    // Special handling for backend errors
    if (response.status === 500) {
      console.error('Player Profile API 500 Error');
      console.error('Full error details:', JSON.stringify(error, null, 2));
      console.error('Error message:', error.message || error.error);
      
      // Show the actual backend error message instead of generic one
      const errorMsg = error.message || error.error || error.details || 'Player profile system error';
      throw new Error(`Backend error: ${errorMsg}`);
    }
    
    // Special handling for user/profile not found
    if (response.status === 404) {
      const errorData = error.message || error.error || '';
      console.error('404 Error details:', error);
      
      // Check various error message formats
      if (typeof errorData === 'string' && errorData.toLowerCase().includes('user not found')) {
        throw new Error('USER_NOT_FOUND');
      }
      
      // For profile endpoints, provide more specific error
      if (response.url && response.url.includes('/playerProfiles/')) {
        throw new Error(`Backend error: ${errorData || 'Profile endpoint not found'}`);
      }
      
      throw new Error('PROFILE_NOT_FOUND');
    }
    
    // Special handling for bad request (validation errors)
    if (response.status === 400) {
      const details = error.details ? ` Details: ${JSON.stringify(error.details)}` : '';
      const fields = error.errors ? ` Fields: ${Object.keys(error.errors).join(', ')}` : '';
      throw new Error(error.message || `Invalid data provided.${details}${fields}`);
    }
    
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

class PlayerProfileService {
  // ============ PROFILE MANAGEMENT ============

  /**
   * Get enhanced player profile with privacy-aware data
   */
  async getEnhancedProfile(userId, options = {}) {
    const { sections = 'all', includeAnalytics = false } = options;
    const params = new URLSearchParams({
      sections,
      includeAnalytics: includeAnalytics.toString()
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/enhanced/${userId}?${params}`,
        {
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }


  /**
   * Create or update player profile
   */
  async updateProfile(userId, profileData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}`,
        {
          method: 'PUT',
          headers: await getAuthHeaders(),
          body: JSON.stringify(profileData)
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Create a new player profile (alternative method)
   */
  async createProfile(profileData) {
    try {
      // Extract userId from profileData
      const { userId, ...profileFields } = profileData;
      
      if (!userId) {
        throw new Error('userId is required to create a profile');
      }
      
      const url = `${API_BASE_URL}/api/playerProfiles/${userId}`;
      const headers = await getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(profileFields)
      });

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update privacy settings for a single section
   */
  async updatePrivacySetting(userId, section, level) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/privacy`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ section, level })
      }
    );

    return handleResponse(response);
  }

  /**
   * Bulk update privacy settings
   */
  async updatePrivacyBulk(userId, settings) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/privacy/bulk`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ settings })
      }
    );

    return handleResponse(response);
  }

  // ============ SKILLS & ENDORSEMENTS ============

  /**
   * Add a skill to profile
   */
  async addSkill(userId, category, skill) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/skills`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ category, skill })
      }
    );

    return handleResponse(response);
  }

  /**
   * Update a skill
   */
  async updateSkill(userId, skillId, updates) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/skills/${skillId}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );

    return handleResponse(response);
  }

  /**
   * Delete a skill
   */
  async deleteSkill(userId, skillId) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/skills/${skillId}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  /**
   * Endorse a skill
   */
  async endorseSkill(userId, skillId, comment) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/skills/${skillId}/endorse`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ comment })
      }
    );

    return handleResponse(response);
  }

  /**
   * Remove endorsement
   */
  async removeEndorsement(userId, skillId, endorsementId) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/skills/${skillId}/endorsements/${endorsementId}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  // ============ EXPERIENCE MANAGEMENT ============

  /**
   * Add experience entry
   */
  async addExperience(userId, experience) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/experience`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(experience)
      }
    );

    return handleResponse(response);
  }

  /**
   * Update experience entry
   */
  async updateExperience(userId, experienceId, updates) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/experience/${experienceId}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );

    return handleResponse(response);
  }

  /**
   * Delete experience entry
   */
  async deleteExperience(userId, experienceId) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/experience/${experienceId}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  // ============ MEDIA MANAGEMENT ============

  /**
   * Upload media (photo, video, document)
   */
  async uploadMedia(userId, formData) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/media`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // Don't set Content-Type for FormData
        },
        body: formData
      }
    );

    return handleResponse(response);
  }

  /**
   * Update media details
   */
  async updateMedia(userId, mediaId, updates) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/media/${mediaId}`,
      {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );

    return handleResponse(response);
  }

  /**
   * Delete media
   */
  async deleteMedia(userId, mediaId) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${userId}/media/${mediaId}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders()
      }
    );

    return handleResponse(response);
  }

  // ============ ANALYTICS ============

  /**
   * Get profile analytics
   */
  async getAnalytics(userId, timeframe = '30d') {
    try {
      const params = new URLSearchParams({ timeframe });

      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}/analytics?${params}`,
        {
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Track profile view
   */
  async trackProfileView(profileId, viewData) {
    const response = await fetch(
      `${API_BASE_URL}/api/playerProfiles/${profileId}/views`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(viewData)
      }
    );

    return handleResponse(response);
  }

  // ============ DATA AGGREGATION ============

  /**
   * Get aggregated awards
   */
  async getAggregatedAwards(userId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}/awards/aggregated`,
        {
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get event participation stats
   */
  async getEventStats(userId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}/events/stats`,
        {
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Calculate profile completeness
   */
  async calculateCompleteness(userId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}/calculate-completeness`,
        {
          method: 'POST',
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  // ============ CLUB HISTORY ============

  /**
   * Get comprehensive club history with memberships, programs, and RSVP stats
   */
  async getClubHistory(userId, options = {}) {
    const { currentOnly = false, includeRsvpStats = true } = options;
    const params = new URLSearchParams();
    
    if (currentOnly) params.append('currentOnly', 'true');
    if (!includeRsvpStats) params.append('includeRsvpStats', 'false');

    try {
      const queryString = params.toString();
      const response = await fetch(
        `${API_BASE_URL}/api/playerProfiles/${userId}/club-history${queryString ? `?${queryString}` : ''}`,
        {
          headers: await getAuthHeaders()
        }
      );

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  }
}

const playerProfileService = new PlayerProfileService();
export default playerProfileService;
